<template>
  <div
    class="
      col-span-6
      sm:col-span-3
      xl:col-span-2
      flex flex-col
      justify-end
      items-center
    "
    v-if="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>
  <div v-if="!loading">
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <CrosshairIcon />
      <h2 class="text-lg font-medium mr-auto">{{ $t('customer.detail') }}</h2>
      <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
        <router-link
          :to="{ name: 'admin-employees-customers' }"
          tag="a"
          class="btn shadow-md flex items-center"
        >
          <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
        </router-link>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="
          flex flex-col
          lg:flex-row
          border-b border-gray-200
          dark:border-dark-5
          pb-5
          -mx-5
        "
      >
        <div
          class="flex flex-1 px-5 items-center justify-center lg:justify-start"
        >
          <div class="ml-5">
            <div
              class="
                w-24
                sm:w-40
                truncate
                sm:whitespace-normal
                font-medium
                text-lg
              "
            >
              {{ customer.name }} {{ customer.lastname }}
            </div>
            <div class="text-gray-600">
              {{ customer.email }}
            </div>
          </div>
        </div>
        <div
          class="
            mt-6
            lg:mt-0
            flex-1
            dark:text-gray-300
            px-5
            border-l border-r border-gray-200
            dark:border-dark-5
            border-t
            lg:border-t-0
            pt-5
            lg:pt-0
          "
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            {{ $t('common.detail') }}
          </div>
          <div
            class="
              flex flex-col
              justify-center
              items-center
              lg:items-start
              mt-4
            "
          >
            <div
              class="
                col-span-6
                sm:col-span-3
                xl:col-span-2
                flex flex-col
                justify-end
                items-center
              "
              v-if="this.isPointProcessing"
            >
              <LoadingIcon icon="three-dots" class="w-8 h-8" />
            </div>
            <div v-if="!isPointProcessing">
              <div class="flex">
                <div class="px-5 pb-8 pt-4 text-left flex-1">
                  {{ $t('common.pointStatus') }}: {{ customer.account.points }}
                </div>
                <div class="px-5 pb-8 text-left flex-1">
                  <input
                    id="points"
                    v-model.trim="validate.points.$model"
                    type="number"
                    name="points"
                    class="
                  intro-y
                  form-control
                  py-3
                  px-4
                  box
                  pr-10
                  placeholder-theme-13
                "
                    :class="{
                      'border-theme-6': validate.points.$error
                    }"
                  />
                  <template v-if="validate.points.$error">
                    <div
                      v-for="(error, index) in validate.points.$errors"
                      :key="index"
                      class="text-theme-6 mt-2"
                    >
                      {{ $t(error.$message) }}
                    </div>
                  </template>
                </div>
              </div>
              <div class="flex">
                <div class="px-5 pb-8 text-center flex-1">
                  <button
                    type="button"
                    @click="addPointConfirm"
                    v-show="this.isPointConfirm === null"
                    class="
                btn btn-success-soft
                w-40
                dark:border-dark-5 dark:text-gray-300
                mr-1
              "
                  >
                    {{ $t('common.add') }}
                  </button>
                  <button
                    type="button"
                    @click="addPointConfirm"
                    v-show="this.isPointConfirm === 'add'"
                    class="
                btn btn-success
                w-40
                dark:border-dark-5 dark:text-gray-300
                mr-1
              "
                  >
                    {{ $t('common.addConfirmPoints') }}
                  </button>
                </div>
                <div
                  class="px-5 pb-8 text-center flex-1"
                  v-if="customer.account.points > 0"
                >
                  <button
                    type="button"
                    @click="removePointConfirm"
                    v-show="this.isPointConfirm === null"
                    class="
                btn btn-warning-soft
                w-40
                dark:border-dark-5 dark:text-gray-300
                mr-1
              "
                  >
                    {{ $t('common.remove') }}
                  </button>
                  <button
                    type="button"
                    @click="removePointConfirm"
                    v-show="this.isPointConfirm === 'remove'"
                    class="
                btn btn-warning
                w-40
                dark:border-dark-5 dark:text-gray-300
                mr-1
              "
                  >
                    {{ $t('common.removeConfirmPoints') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="intro-y box p-5 mt-5">
      <EmployeeCalendar :customer="this.$route.params.uuid" />
    </div>
  </div>
  <!-- BEGIN: Failed Notification Content -->
  <div
    id="failed-load-notification-content"
    class="toastify-content hidden flex"
  >
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('customer.notLoaded') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, ref, reactive, toRefs } from 'vue'
import accountsApi from '@/services/accounts/account.api.js'
import customerApi from '@/services/customers/customer.api'
import Toastify from 'toastify-js'
import EmployeeCalendar from '@/components/reservations/EmployeeCalendar.vue'
import { required, helpers, minValue, integer } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

export default defineComponent({
  components: {
    EmployeeCalendar
  },
  methods: {
    addPointConfirm() {
      this.validate.$touch()
      if (this.validate.$invalid) {
        return
      }
      if (this.isPointConfirm !== 'add') {
        this.isPointConfirm = 'add'
        return
      }
      this.isPointProcessing = true

      const data = {
        userUUID: this.customer.uuid,
        points: this.formData.points
      }
      accountsApi.employeeAdd(data).then(
        response => {
          Toastify({
            node: cash('#success-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.customer.account.points = response.data.points
          this.isPointConfirm = null
          this.isPointProcessing = false
          this.formData.points = 0
        },
        error => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isPointConfirm = null
          this.isPointProcessing = false
        }
      )
    },
    removePointConfirm() {
      this.validate.$touch()
      if (this.validate.$invalid) {
        return
      }
      if (this.isPointConfirm !== 'remove') {
        this.isPointConfirm = 'remove'
        return
      }
      this.isPointProcessing = true

      const data = {
        userUUID: this.customer.uuid,
        points: this.formData.points
      }
      accountsApi.employeeRemove(data).then(
        response => {
          Toastify({
            node: cash('#success-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.customer.account.points = response.data.points
          this.isPointConfirm = null
          this.isPointProcessing = false
          this.formData.points = 0
        },
        error => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isPointConfirm = null
          this.isPointProcessing = false
        }
      )
    }
  },
  computed: {
    console: () => console
  },
  data() {
    return {
      customer: {},
      isPointConfirm: null,
      isPointProcessing: false
    }
  },
  mounted() {
    customerApi.employeeGetDetail(this.$route.params.uuid).then(
      response => {
        this.customer = response.data
        this.loading = false
      },
      error => {
        console.log(error)
        Toastify({
          node: cash('#failed-notification-content')
            .clone()
            .removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        this.$router.push({ name: 'admin-employees-customers' })
      }
    )
  },
  setup() {
    const loading = ref(true)

    const formData = reactive({
      points: 0
    })
    const rules = {
      points: {
        required: helpers.withMessage('form.validation.points', required),
        integer,
        minValue: helpers.withMessage('form.validation.points', minValue(0))
      }
    }
    const validate = useVuelidate(rules, toRefs(formData))

    return {
      validate,
      formData,
      loading
    }
  }
})
</script>
